import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";

export const MAX_ROUTE_POINTS = 30;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export interface RouteOptions {
  maxPoints: number;
  repeatBoundary: boolean;
}

export const DEFAULT_OPTIONS: Readonly<RouteOptions> = {
  maxPoints: MAX_ROUTE_POINTS,
  repeatBoundary: true
};

export interface RoutePoint {
  name: string;
  latitude: number;
  longitude: number;
  sourceIndex: number;
}

export interface ParsedKml {
  baseName: string;
  points: RoutePoint[];
  sectionCount: number;
  mergedJunctions: number;
}

export interface Journey {
  aller: RoutePoint[][];
  retour: RoutePoint[][];
}

export interface GeneratedFile {
  name: string;
  routeName: string;
  direction: "Aller" | "Retour";
  part: number;
  pointCount: number;
  content: string;
}

export class PapaGpsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PapaGpsError";
  }
}

function directChildByLocalName(element: Element, localName: string): Element | undefined {
  return Array.from(element.children).find((child) => child.localName === localName);
}

function firstDescendantByLocalName(element: Element, localName: string): Element | undefined {
  return Array.from(element.getElementsByTagNameNS("*", localName))[0];
}

function parseCoordinate(pointElement: Element, pointName: string): Pick<RoutePoint, "latitude" | "longitude"> {
  const coordinatesElement = firstDescendantByLocalName(pointElement, "coordinates");
  const firstCoordinate = coordinatesElement?.textContent?.trim().split(/\s+/)[0];
  const parts = firstCoordinate?.split(",") ?? [];
  if (!parts[0]?.trim() || !parts[1]?.trim()) {
    throw new PapaGpsError(`L’étape « ${pointName} » contient des coordonnées invalides.`);
  }
  const longitude = Number(parts[0]);
  const latitude = Number(parts[1]);

  if (
    parts.length < 2 ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    throw new PapaGpsError(`L’étape « ${pointName} » contient des coordonnées invalides.`);
  }

  return { latitude, longitude };
}

export function sanitizeBaseName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.(kml|kmz|gpx)$/i, "");
  const cleaned = withoutExtension
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/g, "");

  const baseName = cleaned || "Trajet";
  const reservedWindowsName = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;
  return reservedWindowsName.test(baseName) ? `Trajet ${baseName}` : baseName;
}

function parseXml(text: string, rootName: string): Document {
  if (/<!DOCTYPE/i.test(text)) {
    throw new PapaGpsError("Ce fichier contient une déclaration XML non prise en charge. Exportez à nouveau le trajet.");
  }
  const parser = new DOMParser();
  const document = parser.parseFromString(text, "application/xml");
  if (document.getElementsByTagNameNS("*", "parsererror").length || document.documentElement.localName !== rootName) {
    throw new PapaGpsError(`Ce fichier ${rootName.toUpperCase()} ne peut pas être lu. Exportez à nouveau le trajet.`);
  }
  return document;
}

function requireJourneyPoints(points: readonly RoutePoint[]): void {
  if (points.length < 2) {
    throw new PapaGpsError("Il faut au moins un départ et une arrivée. Aucune étape exploitable ou une seule étape a été trouvée.");
  }
}

function sameLocation(a: RoutePoint, b: RoutePoint): boolean {
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

function sectionOf(placemark: Element): Element {
  let parent = placemark.parentElement;
  while (parent && parent.localName !== "Folder" && parent.localName !== "Document") {
    parent = parent.parentElement;
  }
  return parent ?? placemark.ownerDocument.documentElement;
}

export function parseKmlText(kmlText: string, fileName: string): ParsedKml {
  const document = parseXml(kmlText, "kml");

  const placemarks = Array.from(document.getElementsByTagNameNS("*", "Placemark"));
  const points: RoutePoint[] = [];
  const sections = new Set<Element>();
  let previousSection: Element | undefined;
  let sourceIndex = 0;
  let mergedJunctions = 0;

  for (const placemark of placemarks) {
    const pointElement = firstDescendantByLocalName(placemark, "Point");
    if (!pointElement) {
      continue;
    }

    sourceIndex += 1;
    const nameElement = directChildByLocalName(placemark, "name");
    const name = nameElement?.textContent?.trim() || `Étape ${sourceIndex}`;
    const coordinates = parseCoordinate(pointElement, name);

    const point: RoutePoint = {
      name,
      ...coordinates,
      sourceIndex
    };
    const section = sectionOf(placemark);
    sections.add(section);
    const previousPoint = points.at(-1);
    // Only merge a shared endpoint when crossing from one source section to another.
    if (previousPoint && previousSection !== section && sameLocation(previousPoint, point)) {
      mergedJunctions += 1;
    } else {
      points.push(point);
    }
    previousSection = section;
  }

  if (points.length === 0) {
    throw new PapaGpsError("Aucune étape n’a été trouvée. Exportez le KML complet avec les étapes ; un tracé seul ou un lien vers la carte ne suffit pas.");
  }
  requireJourneyPoints(points);

  return {
    baseName: sanitizeBaseName(fileName),
    points,
    sectionCount: sections.size,
    mergedJunctions
  };
}

export function parseGpxText(text: string, fileName: string): ParsedKml {
  const document = parseXml(text, "gpx");
  const routes = Array.from(document.documentElement.children).filter((child) => child.localName === "rte");
  if (routes.length !== 1) {
    throw new PapaGpsError("Choisissez un GPX contenant un seul itinéraire, avec ses étapes. Les traces seules et les fichiers regroupant plusieurs itinéraires ne sont pas pris en charge.");
  }
  const points = Array.from(routes[0]!.children)
    .filter((child) => child.localName === "rtept")
    .map((element, index): RoutePoint => {
      const name = directChildByLocalName(element, "name")?.textContent?.trim() || `Étape ${index + 1}`;
      const lat = element.getAttribute("lat");
      const lon = element.getAttribute("lon");
      const latitude = Number(lat);
      const longitude = Number(lon);
      if (!lat?.trim() || !lon?.trim() || !Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        throw new PapaGpsError(`L’étape « ${name} » contient des coordonnées invalides.`);
      }
      return { name, latitude, longitude, sourceIndex: index + 1 };
    });
  requireJourneyPoints(points);
  return { baseName: sanitizeBaseName(fileName), points, sectionCount: 1, mergedJunctions: 0 };
}

export function parseKmzBytes(bytes: Uint8Array, fileName: string): ParsedKml {
  if (bytes.byteLength > MAX_FILE_BYTES) {
    throw new PapaGpsError("Le fichier est trop volumineux (10 Mo maximum).");
  }
  try {
    let totalSize = 0;
    const entries = unzipSync(bytes, {
      filter: (entry) => {
        if (!/\.kml$/i.test(entry.name)) return false;
        totalSize += entry.originalSize;
        if (totalSize > MAX_FILE_BYTES) {
          throw new PapaGpsError("Le KML contenu dans cette archive est trop volumineux (10 Mo maximum).");
        }
        return true;
      }
    });
    const names = Object.keys(entries);
    if (names.length !== 1) {
      throw new PapaGpsError("Ce KMZ doit contenir un seul fichier KML. Exportez à nouveau la carte depuis My Maps.");
    }
    return parseKmlText(strFromU8(entries[names[0]!]!), fileName);
  } catch (error) {
    if (error instanceof PapaGpsError) throw error;
    throw new PapaGpsError("Ce fichier KMZ ne peut pas être ouvert. Exportez à nouveau la carte depuis My Maps.");
  }
}

export async function parseRouteFile(file: File): Promise<ParsedKml> {
  if (!/\.(kml|kmz|gpx)$/i.test(file.name)) {
    throw new PapaGpsError("Choisissez un fichier KML, KMZ ou GPX contenant un itinéraire.");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new PapaGpsError("Le fichier est trop volumineux (10 Mo maximum).");
  }
  try {
    if (/\.kmz$/i.test(file.name)) {
      return parseKmzBytes(new Uint8Array(await file.arrayBuffer()), file.name);
    }
    const text = await file.text();
    return /\.gpx$/i.test(file.name) ? parseGpxText(text, file.name) : parseKmlText(text, file.name);
  } catch (error) {
    if (error instanceof PapaGpsError) throw error;
    throw new PapaGpsError("Le fichier n’a pas pu être ouvert. Essayez de le sélectionner à nouveau.");
  }
}

export function validateOptions(options: RouteOptions): void {
  if (!Number.isInteger(options.maxPoints) || options.maxPoints < 2 || options.maxPoints > MAX_ROUTE_POINTS) {
    throw new PapaGpsError(`Choisissez un nombre entier de points entre 2 et ${MAX_ROUTE_POINTS}.`);
  }
}

export function splitWithSharedBoundary(
  points: readonly RoutePoint[],
  maxPoints = MAX_ROUTE_POINTS,
  repeatBoundary = true
): RoutePoint[][] {
  validateOptions({ maxPoints, repeatBoundary });
  requireJourneyPoints(points);
  if (!repeatBoundary && maxPoints === 2 && points.length % 2 === 1) {
    throw new PapaGpsError("Avec deux points par fichier et un nombre impair d’étapes, activez le raccordement ou choisissez une limite d’au moins trois points.");
  }

  const chunks: RoutePoint[][] = [];
  let startIndex = 0;

  while (startIndex < points.length - 1) {
    let size = Math.min(maxPoints, points.length - startIndex);
    // Without overlap, move one point to the last part rather than emit a one-point route.
    if (!repeatBoundary && points.length - startIndex - size === 1) size -= 1;
    const chunk = points.slice(startIndex, startIndex + size);
    chunks.push([...chunk]);

    if (startIndex + size >= points.length) {
      break;
    }

    startIndex += size - (repeatBoundary ? 1 : 0);
  }

  return chunks;
}

export function createJourney(points: readonly RoutePoint[], options: RouteOptions = DEFAULT_OPTIONS): Journey {
  return {
    aller: splitWithSharedBoundary(points, options.maxPoints, options.repeatBoundary),
    retour: splitWithSharedBoundary([...points].reverse(), options.maxPoints, options.repeatBoundary)
  };
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function formatCoordinate(value: number): string {
  const normalized = Object.is(value, -0) ? 0 : value;
  return normalized.toFixed(9);
}

export function createGpx(routeName: string, points: readonly RoutePoint[]): string {
  if (points.length < 2 || points.length > MAX_ROUTE_POINTS) {
    throw new PapaGpsError(`Une route GPX doit contenir entre 2 et ${MAX_ROUTE_POINTS} étapes.`);
  }

  const safeRouteName = escapeXml(routeName);
  const routePoints = points
    .map(
      (point) =>
        `    <rtept lat="${formatCoordinate(point.latitude)}" lon="${formatCoordinate(point.longitude)}">\n` +
        `      <name>${escapeXml(point.name)}</name>\n` +
        "    </rtept>"
    )
    .join("\n");

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="Papa GPS" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">',
    "  <metadata>",
    `    <name>${safeRouteName}</name>`,
    "  </metadata>",
    "  <rte>",
    `    <name>${safeRouteName}</name>`,
    routePoints,
    "  </rte>",
    "</gpx>",
    ""
  ].join("\n");
}

export function createJourneyFiles(baseName: string, points: readonly RoutePoint[], options: RouteOptions = DEFAULT_OPTIONS): GeneratedFile[] {
  const journey = createJourney(points, options);
  const generatedFiles: GeneratedFile[] = [];

  const appendDirection = (direction: "Aller" | "Retour", chunks: RoutePoint[][]): void => {
    chunks.forEach((chunk, index) => {
      const part = index + 1;
      const routeName = `${baseName} ${direction} ${String(part).padStart(Math.max(2, String(chunks.length).length), "0")}`;
      generatedFiles.push({
        name: `${routeName}.gpx`,
        routeName,
        direction,
        part,
        pointCount: chunk.length,
        content: createGpx(routeName, chunk)
      });
    });
  };

  appendDirection("Aller", journey.aller);
  appendDirection("Retour", journey.retour);
  return generatedFiles;
}

export function createZip(baseName: string, files: readonly GeneratedFile[]): { name: string; blob: Blob } {
  const entries: Record<string, Uint8Array> = {};
  for (const file of files) {
    entries[file.name] = strToU8(file.content);
  }

  const zipBytes = zipSync(entries, { level: 6 });
  const blob = new Blob([zipBytes], { type: "application/zip" });
  return {
    name: `${baseName} - Garmin.zip`,
    blob
  };
}
